import { TableComponent } from 'baseComponents/TableComponent';
import dayjs from 'dayjs';
import { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router';
import { Col, Flex } from 'antd';
import { ScreenWrapperComponent } from 'components/layouts/ScreenWrapperComponent';
import { ResultPage } from 'components/ResultPage';
import { useGetCampaignsAnalytics } from 'hooks/useGetCampaignsAnalytics';
import { TableFilter } from 'baseComponents/TableComponent/TableFilter/TableFilter';
import {
  tableFilters,
  tableSorters,
} from 'baseComponents/TableComponent/table.utils';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { useGetCampaignAnalyticsExport } from 'hooks/useGetCampaignAnalyticsExport';

export type CampaignsAnalyticsFlatten = {
  id: number;
  campaign_name: string;
  user_email: string;
  campaign_scheduled: string;
  messages_sent: number | null;
  conversion_rate: number | null;
  total_revenue: string | null;
};

const columns: ColumnsType<CampaignsAnalyticsFlatten> = [
  { title: 'Campaign Name', dataIndex: 'campaign_name', key: 'campaign_name' },
  {
    title: 'Scheduled Date',
    dataIndex: 'campaign_scheduled',
    key: 'campaign_scheduled',
    sorter: tableSorters.string('campaign_scheduled'),
    defaultSortOrder: 'descend',
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'User Email',
    dataIndex: 'user_email',
    key: 'user_email',
    sorter: tableSorters.string('user_email'),
    sortDirections: ['descend', 'ascend'],
    onFilter: tableFilters.includes('user_email'),
    filterDropdown: (args) => <TableFilter dataIndex='user_email' {...args} />,
  },
  {
    title: 'Messages Sent',
    dataIndex: 'sends',
    key: 'sends',
    sorter: tableSorters.number('sends'),
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Channel',
    dataIndex: 'channel',
    key: 'channel',
    sorter: tableSorters.string('channel'),
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Revenue *',
    dataIndex: 'total_revenue',
    key: 'total_revenue',
    sorter: tableSorters.string('total_revenue'),
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Conversion Rate',
    dataIndex: 'conversion_rate',
    key: 'conversion_rate',
    sorter: tableSorters.number('conversion_rate'),
    sortDirections: ['descend', 'ascend'],
  },
];

export const CampaignAnalyticsScreen = () => {
  const navigate = useNavigate();

  const handleCampaignClick = (record: CampaignsAnalyticsFlatten) => {
    navigate(`/campaign-analytics/${record.id}`);
  };

  const {
    data = [],
    isLoading,
    isRefetching,
    isError,
    error,
    refetch,
  } = useGetCampaignsAnalytics();

  const {
    refetch: getExport,
    isLoading: isExportLoading,
    isRefetching: isExportRefetching,
  } = useGetCampaignAnalyticsExport();

  if (isError) {
    return (
      <ResultPage
        title={error.response?.data?.message || 'Error fetching campaigns'}
      />
    );
  }

  const parsedCampaignsAnalyticsData = () => {
    if (data) {
      return data.map((obj) => {
        const { campaign, ...rest } = obj;
        return {
          ...rest,
          sends: rest.sends ?? 0,
          campaign_name: campaign.name,
          user_email: campaign.user_email,
          campaign_scheduled: dayjs(campaign.scheduled).format(
            'YYYY-MM-DD HH:mm'
          ),
          total_revenue: `£${Number(rest.total_revenue?.toFixed(2)) || 0}`,
          conversion_rate: Number(rest.conversion_rate?.toFixed(2)) || 0,
          key: obj.id,
          channel: obj.channel,
        };
      });
    }
    return [];
  };

  return (
    <ScreenWrapperComponent>
      <Col md={{ pull: 3, push: 3, span: 18 }}>
        <Flex justify='end' gap={16}>
          <ButtonComponent
            label='Refresh'
            appereance='primary'
            size='middle'
            style={{ maxWidth: 320 }}
            disabled={isLoading || isRefetching}
            onClick={refetch}
          />
          <ButtonComponent
            label='Export to CSV'
            appereance='primary'
            size='middle'
            style={{ maxWidth: 320 }}
            disabled={
              data.length === 0 || isExportLoading || isExportRefetching
            }
            onClick={() => {
              getExport();
            }}
          />
        </Flex>
        <TableComponent
          onRow={(record) => ({ onClick: () => handleCampaignClick(record) })}
          tableClassname='campaign-analytics-table'
          loading={isLoading || isRefetching}
          dataSource={parsedCampaignsAnalyticsData()}
          total={data.length}
          columns={columns}
        />
        <p style={{ fontSize: 14, marginTop: 8 }}>
          Campaign Analytics might take up to 3 days to be fully updated.
          <br />
          * Push revenue includes purchases made within 1 day of receiving the
          notification.
        </p>
      </Col>
    </ScreenWrapperComponent>
  );
};
